/**
 * Session continuity across reloads and tabs without persisting tokens (ADR-0022).
 *
 * Two small, non-secret facts are kept:
 * - a *hint* in localStorage that this browser had a session, so a reload or a deep link tries silent restoration
 *   from the identity provider instead of showing the sign-in page first. It carries no identity and grants
 *   nothing: a stale hint only costs one `prompt=none` round trip that answers `login_required`.
 * - a per-tab guard in sessionStorage so the top-level `prompt=none` fallback runs at most once per page load
 *   chain and can never loop.
 *
 * Sign-in and sign-out are announced on a BroadcastChannel so other tabs follow.
 */

const HINT_KEY = 'zagru.auth.hint';
const TOP_LEVEL_GUARD_KEY = 'zagru.auth.silent-top-level';
const CHANNEL = 'zagru.auth';

/*
 * The channel's name, which tests may narrow.
 *
 * `BroadcastChannel` is how tabs of one application follow each other's sign-in and sign-out. Under a test
 * runner the "tabs" are unrelated test files, and Node's implementation carries messages across worker
 * threads — so one file completing a sign-in authenticated a provider mounted in another, which then issued
 * the session reads that file was asserting it had not made. The isolation a browser gives for free is
 * restored by giving each worker a name of its own.
 */
let channelName = CHANNEL;

/** Narrows the channel to one name (tests). Closes any channel already open under the previous name. */
export function setAuthBroadcastChannel(name: string): void {
  resetAuthBroadcast();
  channelName = name;
}

export type AuthBroadcast = { type: 'signed-in' } | { type: 'signed-out' };

function local(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function session(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export const sessionHint = {
  present(): boolean {
    try {
      return local()?.getItem(HINT_KEY) === '1';
    } catch {
      return false;
    }
  },
  set(): void {
    try {
      local()?.setItem(HINT_KEY, '1');
    } catch {
      /* storage unavailable: restoration falls back to the sign-in page */
    }
  },
  clear(): void {
    try {
      local()?.removeItem(HINT_KEY);
    } catch {
      /* ignore */
    }
  },
};

/** One top-level `prompt=none` attempt per tab until a session is established again. */
export const topLevelSilentGuard = {
  tried(): boolean {
    try {
      return session()?.getItem(TOP_LEVEL_GUARD_KEY) === '1';
    } catch {
      return true;
    }
  },
  mark(): void {
    try {
      session()?.setItem(TOP_LEVEL_GUARD_KEY, '1');
    } catch {
      /* ignore */
    }
  },
  reset(): void {
    try {
      session()?.removeItem(TOP_LEVEL_GUARD_KEY);
    } catch {
      /* ignore */
    }
  },
};

let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(channelName) : null;
  return channel;
}

export function broadcastAuth(message: AuthBroadcast): void {
  try {
    getChannel()?.postMessage(message);
  } catch {
    /* a closed channel is not worth failing a sign-in over */
  }
}

export function onAuthBroadcast(listener: (message: AuthBroadcast) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => undefined;
  const handler = (event: MessageEvent) => {
    const data = event.data as Partial<AuthBroadcast> | null;
    if (data && (data.type === 'signed-in' || data.type === 'signed-out')) listener(data as AuthBroadcast);
  };
  ch.addEventListener('message', handler);
  return () => ch.removeEventListener('message', handler);
}

/** Closes the channel (tests). */
export function resetAuthBroadcast(): void {
  channel?.close();
  channel = undefined;
}
